import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Upload, Trash2, Download, Wand2, Check, Star, StarOff, Power, PowerOff, FileText, Loader2, Edit3 } from 'lucide-react'
import api from '../../../core/services/api'
import { Toast } from '../../../components/Toast'
import ConfirmModal from '../../../components/ConfirmModal'
import { TemplateDocumento, PlaceholderSuggestion, AnalisiResult, TIPI_TEMPLATE, TipoTemplate } from '../../../types/template'

export default function ModelliStampaPage() {
  const navigate = useNavigate()
  const [templates, setTemplates] = useState<TemplateDocumento[]>([])
  const [loading, setLoading] = useState(true)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)

  // Modal nuovo template
  const [showNewModal, setShowNewModal] = useState(false)
  const [newTemplate, setNewTemplate] = useState({
    nome: '',
    tipo: 'offerta' as TipoTemplate,
    descrizione: '',
  })
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)

  // Modal analisi AI
  const [showAnalisiModal, setShowAnalisiModal] = useState(false)
  const [analisiTemplate, setAnalisiTemplate] = useState<TemplateDocumento | null>(null)
  const [analisiResult, setAnalisiResult] = useState<AnalisiResult | null>(null)
  const [analisiLoading, setAnalisiLoading] = useState(false)
  const [mapping, setMapping] = useState<Record<string, string>>({})
  const [applyingMapping, setApplyingMapping] = useState(false)

  // Placeholder disponibili
  const [placeholderDisponibili, setPlaceholderDisponibili] = useState<Record<string, string>>({})

  // Helper per estrarre messaggi di errore (gestisce sia stringhe che errori Pydantic)
  const extractErrorMessage = (error: any, fallback: string): string => {
    const detail = error?.response?.data?.detail
    if (typeof detail === 'string') return detail
    if (Array.isArray(detail) && detail.length > 0) {
      return detail.map((e: any) => e.msg || e.message || 'Errore validazione').join(', ')
    }
    return fallback
  }

  useEffect(() => {
    loadTemplates()
    loadPlaceholder()
  }, [])

  const loadTemplates = async () => {
    try {
      const res = await api.get('/v1/template-documenti')
      setTemplates(res.data)
    } catch (error) {
      setToast({ message: 'Errore caricamento template', type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const loadPlaceholder = async () => {
    try {
      const res = await api.get('/v1/template-documenti/placeholder-disponibili')
      setPlaceholderDisponibili(res.data)
    } catch (error) {
      console.error('Errore caricamento placeholder', error)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (!file.name.endsWith('.docx')) {
        setToast({ message: 'Solo file .docx accettati', type: 'error' })
        return
      }
      setSelectedFile(file)
    }
  }

  const handleCreateTemplate = async () => {
    if (!newTemplate.nome || !selectedFile) {
      setToast({ message: 'Nome e file obbligatori', type: 'warning' })
      return
    }

    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('nome', newTemplate.nome)
      formData.append('tipo', newTemplate.tipo)
      if (newTemplate.descrizione) {
        formData.append('descrizione', newTemplate.descrizione)
      }
      formData.append('file', selectedFile)

      // Rimuovi Content-Type per permettere ad Axios di settare multipart/form-data con boundary
      await api.post('/v1/template-documenti', formData, {
        headers: { 'Content-Type': undefined }
      })
      setToast({ message: 'Template creato', type: 'success' })
      setShowNewModal(false)
      setNewTemplate({ nome: '', tipo: 'offerta', descrizione: '' })
      setSelectedFile(null)
      loadTemplates()
    } catch (error: any) {
      setToast({ message: extractErrorMessage(error, 'Errore creazione'), type: 'error' })
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/v1/template-documenti/${id}`)
      setToast({ message: 'Template eliminato', type: 'success' })
      loadTemplates()
    } catch (error) {
      setToast({ message: 'Errore eliminazione', type: 'error' })
    }
    setConfirmDelete(null)
  }

  const handleToggleActive = async (template: TemplateDocumento) => {
    try {
      await api.put(`/v1/template-documenti/${template.id}/attiva`)
      setToast({ message: template.is_active ? 'Template disattivato' : 'Template attivato', type: 'success' })
      loadTemplates()
    } catch (error) {
      setToast({ message: 'Errore', type: 'error' })
    }
  }

  const handleSetDefault = async (template: TemplateDocumento) => {
    try {
      await api.put(`/v1/template-documenti/${template.id}/default`)
      setToast({ message: 'Template impostato come default', type: 'success' })
      loadTemplates()
    } catch (error) {
      setToast({ message: 'Errore', type: 'error' })
    }
  }

  const handleDownload = async (template: TemplateDocumento, processato: boolean = false) => {
    try {
      const res = await api.get(`/v1/template-documenti/${template.id}/download?processato=${processato}`, {
        responseType: 'blob'
      })
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', processato ? `processato_${template.filename_originale}` : template.filename_originale || 'template.docx')
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (error) {
      setToast({ message: 'Errore download', type: 'error' })
    }
  }

  const handleAnalizza = async (template: TemplateDocumento) => {
    setAnalisiTemplate(template)
    setShowAnalisiModal(true)
    setAnalisiLoading(true)
    setAnalisiResult(null)
    setMapping({})

    try {
      const res = await api.post(`/v1/template-documenti/${template.id}/analizza`)
      setAnalisiResult(res.data)
      // Pre-popola mapping con suggerimenti
      const initialMapping: Record<string, string> = {}
      res.data.suggerimenti.forEach((s: PlaceholderSuggestion) => {
        initialMapping[s.testo_originale] = s.placeholder_suggerito
      })
      setMapping(initialMapping)
    } catch (error: any) {
      setToast({ message: extractErrorMessage(error, 'Errore analisi'), type: 'error' })
      setShowAnalisiModal(false)
    } finally {
      setAnalisiLoading(false)
    }
  }

  const handleApplicaMapping = async () => {
    if (!analisiTemplate) return

    // Filtra i mapping vuoti (quando l'utente seleziona "Nessuno")
    const filteredMapping: Record<string, string> = {}
    Object.entries(mapping).forEach(([key, value]) => {
      if (value && value.trim() !== '') {
        filteredMapping[key] = value
      }
    })

    if (Object.keys(filteredMapping).length === 0) {
      setToast({ message: 'Seleziona almeno un placeholder da applicare', type: 'warning' })
      return
    }

    setApplyingMapping(true)
    try {
      await api.post(`/v1/template-documenti/${analisiTemplate.id}/applica-mapping`, { mapping: filteredMapping })
      setToast({ message: 'Mapping applicato', type: 'success' })
      setShowAnalisiModal(false)
      loadTemplates()
    } catch (error: any) {
      setToast({ message: extractErrorMessage(error, 'Errore applicazione mapping'), type: 'error' })
    } finally {
      setApplyingMapping(false)
    }
  }

  // Helper per identificare se un suggerimento è una tabella
  const isTableSuggestion = (testoOriginale: string): boolean => {
    return testoOriginale.startsWith('{{TABELLA_')
  }

  // Helper per estrarre il numero della tabella
  const getTableNumber = (testoOriginale: string): number | null => {
    const match = testoOriginale.match(/\{\{TABELLA_(\d+)\}\}/)
    return match ? parseInt(match[1]) : null
  }

  // Conta le tabelle nei suggerimenti
  const countTables = (suggerimenti: PlaceholderSuggestion[]): number => {
    return suggerimenti.filter(s => isTableSuggestion(s.testo_originale)).length
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Modelli Stampa</h1>
          <p className="text-gray-500 mt-1">Gestisci i template Word per la generazione documenti</p>
        </div>
        <button
          onClick={() => setShowNewModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <Plus className="w-5 h-5" />
          Nuovo Template
        </button>
      </div>

      {/* Lista Template */}
      {templates.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Nessun template</h3>
          <p className="text-gray-500 mb-6">Carica il tuo primo template Word per iniziare</p>
          <button
            onClick={() => setShowNewModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <Upload className="w-5 h-5" />
            Carica Template
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => (
            <div
              key={template.id}
              className={`bg-white rounded-xl shadow-sm border p-6 ${
                template.is_active ? 'border-gray-100' : 'border-gray-200 bg-gray-50 opacity-75'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900">{template.nome}</h3>
                    {template.is_default && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded">
                        Default
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    {TIPI_TEMPLATE.find(t => t.value === template.tipo)?.label || template.tipo}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleToggleActive(template)}
                    className={`p-2 rounded-lg transition-colors ${
                      template.is_active
                        ? 'text-green-600 hover:bg-green-50'
                        : 'text-gray-400 hover:bg-gray-100'
                    }`}
                    title={template.is_active ? 'Disattiva' : 'Attiva'}
                  >
                    {template.is_active ? <Power className="w-5 h-5" /> : <PowerOff className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={() => handleSetDefault(template)}
                    className={`p-2 rounded-lg transition-colors ${
                      template.is_default
                        ? 'text-yellow-600'
                        : 'text-gray-400 hover:bg-gray-100'
                    }`}
                    title="Imposta come default"
                    disabled={template.is_default}
                  >
                    {template.is_default ? <Star className="w-5 h-5 fill-current" /> : <StarOff className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {template.descrizione && (
                <p className="text-sm text-gray-600 mb-4">{template.descrizione}</p>
              )}

              <div className="text-xs text-gray-400 mb-4">
                {template.filename_originale}
                {template.has_file_processato && (
                  <span className="ml-2 text-green-600">Processato</span>
                )}
              </div>

              <div className="flex items-center gap-2 pt-4 border-t border-gray-100">
                <button
                  onClick={() => handleDownload(template)}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg"
                  title="Scarica originale"
                >
                  <Download className="w-4 h-4" />
                  Originale
                </button>
                {template.has_file_processato && (
                  <button
                    onClick={() => handleDownload(template, true)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm text-green-600 hover:bg-green-50 rounded-lg"
                    title="Scarica processato"
                  >
                    <Download className="w-4 h-4" />
                    Processato
                  </button>
                )}
                <button
                  onClick={() => handleAnalizza(template)}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm text-primary-600 hover:bg-primary-50 rounded-lg"
                  title="Analizza con AI"
                >
                  <Wand2 className="w-4 h-4" />
                  Analizza
                </button>
                <button
                  onClick={() => navigate(`/app/configurazione/modelli-stampa/${template.id}/editor`)}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm text-indigo-600 hover:bg-indigo-50 rounded-lg"
                  title="Editor visuale"
                >
                  <Edit3 className="w-4 h-4" />
                  Editor
                </button>
                <button
                  onClick={() => setConfirmDelete(template.id)}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                  title="Elimina"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Nuovo Template */}
      {showNewModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Nuovo Template</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                <input
                  type="text"
                  value={newTemplate.nome}
                  onChange={(e) => setNewTemplate({ ...newTemplate, nome: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Es. Offerta Standard"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                <select
                  value={newTemplate.tipo}
                  onChange={(e) => setNewTemplate({ ...newTemplate, tipo: e.target.value as TipoTemplate })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  {TIPI_TEMPLATE.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
                <textarea
                  value={newTemplate.descrizione}
                  onChange={(e) => setNewTemplate({ ...newTemplate, descrizione: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  rows={2}
                  placeholder="Descrizione opzionale..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">File Word (.docx)</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-400 transition-colors">
                  <input
                    type="file"
                    accept=".docx"
                    onChange={handleFileChange}
                    className="hidden"
                    id="file-upload"
                  />
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                    {selectedFile ? (
                      <p className="text-sm text-primary-600 font-medium">{selectedFile.name}</p>
                    ) : (
                      <p className="text-sm text-gray-500">Clicca per selezionare un file</p>
                    )}
                  </label>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowNewModal(false)
                  setNewTemplate({ nome: '', tipo: 'offerta', descrizione: '' })
                  setSelectedFile(null)
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Annulla
              </button>
              <button
                onClick={handleCreateTemplate}
                disabled={uploading}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Crea
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Analisi AI */}
      {showAnalisiModal && analisiTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                Analisi AI - {analisiTemplate.nome}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Mappa i testi trovati ai placeholder standard
              </p>
            </div>

            <div className="flex-1 overflow-auto p-6">
              {analisiLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-12 h-12 animate-spin text-primary-600 mb-4" />
                  <p className="text-gray-600">Analisi in corso...</p>
                  <p className="text-sm text-gray-400 mt-1">Potrebbe richiedere alcuni secondi</p>
                </div>
              ) : analisiResult && analisiResult.suggerimenti.length > 0 ? (
                <div className="space-y-4">
                  {/* Warning per tabelle multiple */}
                  {countTables(analisiResult.suggerimenti) > 1 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <p className="text-amber-800 font-medium">
                        ⚠️ Rilevate {countTables(analisiResult.suggerimenti)} tabelle
                      </p>
                      <p className="text-amber-600 text-sm mt-1">
                        Se alcune tabelle non devono essere sostituite, seleziona "Nessuno" nel dropdown.
                        Di solito solo una tabella contiene i prodotti.
                      </p>
                    </div>
                  )}

                  {/* Warning per placeholder non disponibili */}
                  {analisiResult.suggerimenti.some(s => !s.is_valid) && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <p className="text-red-800 font-medium">
                        ❌ Alcuni placeholder non sono disponibili nel sistema
                      </p>
                      <p className="text-red-600 text-sm mt-1">
                        I campi evidenziati in rosso non hanno corrispondenza nei dati disponibili.
                        Puoi mapparli manualmente a un campo esistente o lasciarli su "Nessuno".
                      </p>
                    </div>
                  )}

                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Testo trovato</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Contesto</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Placeholder</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analisiResult.suggerimenti.map((s, i) => {
                        const isTable = isTableSuggestion(s.testo_originale)
                        const tableNum = getTableNumber(s.testo_originale)
                        const isExtraTable = isTable && tableNum && tableNum > 1
                        const isInvalid = !s.is_valid

                        return (
                          <tr
                            key={i}
                            className={`border-b border-gray-100 ${
                              isInvalid
                                ? 'bg-red-50 hover:bg-red-100'
                                : isExtraTable
                                  ? 'bg-amber-50 hover:bg-amber-100'
                                  : isTable
                                    ? 'bg-blue-50 hover:bg-blue-100'
                                    : 'hover:bg-gray-50'
                            }`}
                          >
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2 flex-wrap">
                                {isInvalid && (
                                  <span className="px-2 py-0.5 text-xs font-medium rounded bg-red-200 text-red-800">
                                    Non disponibile
                                  </span>
                                )}
                                {isTable && (
                                  <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                                    isExtraTable ? 'bg-amber-200 text-amber-800' : 'bg-blue-200 text-blue-800'
                                  }`}>
                                    Tabella {tableNum}
                                  </span>
                                )}
                                <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                                  {s.testo_originale.length > 40
                                    ? s.testo_originale.substring(0, 40) + '...'
                                    : s.testo_originale}
                                </code>
                              </div>
                              {isInvalid && s.placeholder_suggerito_originale && (
                                <p className="text-xs text-red-600 mt-1">
                                  Claude ha suggerito: <code className="bg-red-100 px-1 rounded">{s.placeholder_suggerito_originale}</code>
                                  {' '}(non configurato)
                                </p>
                              )}
                              {isExtraTable && !isInvalid && (
                                <p className="text-xs text-amber-600 mt-1">
                                  Da verificare: potrebbe non essere una tabella prodotti
                                </p>
                              )}
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-500">{s.contesto}</td>
                            <td className="py-3 px-4">
                              <select
                                value={mapping[s.testo_originale] || ''}
                                onChange={(e) =>
                                  setMapping({ ...mapping, [s.testo_originale]: e.target.value })
                                }
                                className={`w-full px-2 py-1 border rounded text-sm ${
                                  isInvalid
                                    ? 'border-red-300 bg-red-50'
                                    : isExtraTable
                                      ? 'border-amber-300 bg-amber-50'
                                      : 'border-gray-300'
                                }`}
                              >
                                <option value="">-- Nessuno (non sostituire) --</option>
                                {Object.entries(placeholderDisponibili).map(([ph, desc]) => (
                                  <option key={ph} value={ph}>
                                    {ph} - {desc}
                                  </option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600">Nessun campo dinamico identificato</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Prova a caricare un documento con dati di esempio
                  </p>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowAnalisiModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Annulla
              </button>
              {analisiResult && analisiResult.suggerimenti.length > 0 && (
                <button
                  onClick={handleApplicaMapping}
                  disabled={applyingMapping || Object.values(mapping).every(v => !v)}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {applyingMapping ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                  Applica Mapping
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete Modal */}
      <ConfirmModal
        isOpen={!!confirmDelete}
        title="Elimina Template"
        message="Sei sicuro di voler eliminare questo template? L'operazione non puo essere annullata."
        confirmLabel="Elimina"
        onConfirm={() => confirmDelete && handleDelete(confirmDelete)}
        onCancel={() => setConfirmDelete(null)}
      />

      {/* Toast */}
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </div>
  )
}
